import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { toast } from 'sonner';

const PricingSection = ({ packages }) => {
  const [loadingPackage, setLoadingPackage] = useState(null);

  const handleCheckout = async (packageItem) => {
    setLoadingPackage(packageItem.id);
    try {
      const originUrl = window.location.origin;
      const response = await publicApi.createCheckoutSession(packageItem.id, originUrl);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to initiate checkout. Please try again.');
    } finally {
      setLoadingPackage(null);
    }
  };

  return (
    <section id="pricing" className="section-spacing" data-testid="pricing-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            Choose Your Package
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Flexible pricing options designed to fit your project needs and budget.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={`glass-card p-8 relative ${pkg.popular ? 'pricing-popular border-primary/50' : ''}`}
              data-testid={`pricing-card-${index}`}
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-heading font-bold mb-2">{pkg.name}</h3>
                <p className="text-muted-foreground text-sm mb-6">{pkg.description}</p>
                <div className="flex items-end justify-center gap-1">
                  <span className="text-5xl font-heading font-bold text-primary">
                    ${pkg.price.toLocaleString()}
                  </span>
                  <span className="text-muted-foreground mb-2">/{pkg.currency?.toUpperCase() || 'USD'}</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {pkg.features?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleCheckout(pkg)}
                disabled={loadingPackage === pkg.id}
                className={`w-full ${pkg.popular ? 'btn-primary' : 'btn-outline'}`}
                data-testid={`pricing-checkout-btn-${index}`}
              >
                {loadingPackage === pkg.id ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Get Started'
                )}
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
